import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import {
  Route, Plus, Lock, CheckCircle, PlayCircle, Trash2, Edit, GraduationCap, BookOpen,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  title: '',
  description: '',
  course_ids: [],
  target_cargos: '',
  is_onboarding: false,
  status: 'published',
};

const TrilhasTab = ({ hasAdminAccess, onOpenCourse }) => {
  const [trilhas, setTrilhas] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const loadTrilhas = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/lms/trilhas`);
      setTrilhas(res.data || []);
    } catch (e) {
      toast.error('Erro ao carregar trilhas');
    } finally {
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    try {
      const res = await axios.get(`${API}/api/lms/courses`);
      setCourses(res.data || []);
    } catch (e) {
      // catálogo pode exigir admin; ignora silenciosamente para alunos
    }
  };

  useEffect(() => {
    loadTrilhas();
    if (hasAdminAccess) loadCourses();
  }, [hasAdminAccess]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({
      title: t.title || '',
      description: t.description || '',
      course_ids: t.course_ids || [],
      target_cargos: (t.target_cargos || []).join(', '),
      is_onboarding: !!t.is_onboarding,
      status: t.status || 'published',
    });
    setDialogOpen(true);
  };

  const toggleCourse = (cid) => {
    setForm((prev) => ({
      ...prev,
      course_ids: prev.course_ids.includes(cid)
        ? prev.course_ids.filter((c) => c !== cid)
        : [...prev.course_ids, cid],
    }));
  };

  const moveCourse = (idx, dir) => {
    setForm((prev) => {
      const arr = [...prev.course_ids];
      const ni = idx + dir;
      if (ni < 0 || ni >= arr.length) return prev;
      [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
      return { ...prev, course_ids: arr };
    });
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Informe o título da trilha');
      return;
    }
    if (form.course_ids.length === 0) {
      toast.error('Selecione ao menos um curso');
      return;
    }
    const payload = {
      title: form.title,
      description: form.description,
      course_ids: form.course_ids,
      target_cargos: form.target_cargos
        ? form.target_cargos.split(',').map((s) => s.trim()).filter(Boolean)
        : [],
      is_onboarding: form.is_onboarding,
      status: form.status,
    };
    try {
      if (editingId) {
        await axios.put(`${API}/api/lms/trilhas/${editingId}`, payload);
        toast.success('Trilha atualizada');
      } else {
        await axios.post(`${API}/api/lms/trilhas`, payload);
        toast.success('Trilha criada');
      }
      setDialogOpen(false);
      loadTrilhas();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar trilha');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Arquivar esta trilha?')) return;
    try {
      await axios.delete(`${API}/api/lms/trilhas/${id}`);
      toast.success('Trilha arquivada');
      loadTrilhas();
    } catch (e) {
      toast.error('Erro ao arquivar trilha');
    }
  };

  const openDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/api/lms/trilhas/${id}`);
      setDetail(res.data);
      setDetailOpen(true);
    } catch (e) {
      toast.error('Erro ao carregar trilha');
    }
  };

  const handleEnroll = async (id) => {
    try {
      const res = await axios.post(`${API}/api/lms/trilhas/${id}/inscrever`);
      toast.success(res.data?.message || 'Inscrição realizada');
      openDetail(id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao se inscrever');
    }
  };

  const handleAdvance = async (id) => {
    try {
      const res = await axios.post(`${API}/api/lms/trilhas/${id}/avancar`);
      toast.success(res.data?.message || 'Matriculado no próximo curso');
      openDetail(id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao avançar na trilha');
    }
  };

  const handleStepClick = async (s) => {
    if (!s.unlocked || !onOpenCourse) return;
    // Concluído, em andamento ou visão de admin: apenas abre o curso.
    if (s.completed || s.enrolled || hasAdminAccess) {
      setDetailOpen(false);
      onOpenCourse(s.id);
      return;
    }
    // Aluno ainda não matriculado neste passo: matricula e então abre o curso.
    if (hasAnyEnrollment) {
      await handleAdvance(detail.id);
    } else {
      await handleEnroll(detail.id);
    }
    setDetailOpen(false);
    onOpenCourse(s.id);
  };

  // Próximo passo desbloqueado e ainda não matriculado (para CTA do aluno)
  const nextStep = detail
    ? (detail.steps || []).find((s) => s.unlocked && !s.completed && !s.enrolled)
    : null;
  const hasAnyEnrollment = detail
    ? (detail.steps || []).some((s) => s.enrolled)
    : false;

  const orderedSelected = form.course_ids
    .map((cid) => courses.find((c) => c.id === cid))
    .filter(Boolean);

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Carregando trilhas...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Route className="h-5 w-5 text-blue-600" /> Trilhas de Aprendizagem
          </h2>
          <p className="text-sm text-gray-600">Sequências de cursos com desbloqueio progressivo.</p>
        </div>
        {hasAdminAccess && (
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" /> Nova Trilha
          </Button>
        )}
      </div>

      {trilhas.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Route className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Nenhuma trilha cadastrada.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {trilhas.map((t) => (
            <Card key={t.id} className="flex flex-col">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg flex items-center gap-2">
                    {t.is_onboarding ? <GraduationCap className="h-5 w-5 text-purple-600" /> : <Route className="h-5 w-5 text-blue-600" />}
                    {t.title}
                  </CardTitle>
                  {t.is_onboarding && <Badge variant="secondary">Integração</Badge>}
                </div>
                <CardDescription>{t.description}</CardDescription>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col justify-between space-y-4">
                <div className="text-sm text-gray-600 flex items-center gap-2">
                  <BookOpen className="h-4 w-4" /> {t.course_count} curso(s)
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => openDetail(t.id)}>
                    Ver trilha
                  </Button>
                  {hasAdminAccess && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(t)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(t.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar Trilha' : 'Nova Trilha'}</DialogTitle>
            <DialogDescription>Defina os cursos e a ordem de desbloqueio.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Título</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <Label>Descrição</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <Label>Cargos-alvo (separados por vírgula)</Label>
              <Input value={form.target_cargos} onChange={(e) => setForm({ ...form, target_cargos: e.target.value })} placeholder="Ex.: Analista, Técnico de Segurança" />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="is_onboarding" checked={form.is_onboarding} onCheckedChange={(v) => setForm({ ...form, is_onboarding: !!v })} />
              <Label htmlFor="is_onboarding">Trilha de integração (admissão)</Label>
            </div>

            <div>
              <Label>Cursos disponíveis</Label>
              <div className="border rounded-md max-h-48 overflow-y-auto p-2 space-y-1">
                {courses.length === 0 && <p className="text-sm text-gray-500 p-2">Nenhum curso disponível.</p>}
                {courses.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
                    <Checkbox checked={form.course_ids.includes(c.id)} onCheckedChange={() => toggleCourse(c.id)} />
                    {c.title}
                  </label>
                ))}
              </div>
            </div>

            {orderedSelected.length > 0 && (
              <div>
                <Label>Ordem da trilha</Label>
                <div className="space-y-1">
                  {orderedSelected.map((c, idx) => (
                    <div key={c.id} className="flex items-center justify-between border rounded p-2 text-sm">
                      <span>{idx + 1}. {c.title}</span>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => moveCourse(idx, -1)} disabled={idx === 0}>↑</Button>
                        <Button size="sm" variant="ghost" onClick={() => moveCourse(idx, 1)} disabled={idx === orderedSelected.length - 1}>↓</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle>{detail.title}</DialogTitle>
                <DialogDescription>{detail.description}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Progresso da trilha</span>
                    <span>{detail.progress_percentage}%</span>
                  </div>
                  <Progress value={detail.progress_percentage} className="h-2" />
                </div>
                <div className="space-y-2">
                  {(detail.steps || []).map((s) => (
                    <div
                      key={s.id}
                      onClick={() => handleStepClick(s)}
                      className={`flex items-center justify-between border rounded p-3 ${
                        !s.unlocked ? 'opacity-60' : 'cursor-pointer hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {s.completed ? (
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        ) : s.unlocked ? (
                          <PlayCircle className="h-5 w-5 text-blue-600" />
                        ) : (
                          <Lock className="h-5 w-5 text-gray-400" />
                        )}
                        <div>
                          <p className="font-medium text-sm">{s.order}. {s.title}</p>
                          <p className="text-xs text-gray-500">
                            {s.nr_code ? `${s.nr_code} · ` : ''}{s.duration_hours}h
                            {s.enrolled ? ` · ${s.progress || 0}%` : ''}
                          </p>
                        </div>
                      </div>
                      {s.completed ? (
                        <Badge className="bg-green-100 text-green-800">Concluído</Badge>
                      ) : s.enrolled ? (
                        <Badge variant="secondary">Em andamento</Badge>
                      ) : s.unlocked ? (
                        <Badge variant="secondary">Liberado</Badge>
                      ) : (
                        <Badge variant="outline">Bloqueado</Badge>
                      )}
                    </div>
                  ))}
                </div>
                {!hasAdminAccess && (
                  <>
                    {detail.completed ? (
                      <div className="text-center text-sm text-green-700 bg-green-50 rounded p-3 flex items-center justify-center gap-2">
                        <CheckCircle className="h-4 w-4" /> Trilha concluída!
                      </div>
                    ) : !hasAnyEnrollment ? (
                      <Button className="w-full" onClick={() => handleEnroll(detail.id)}>
                        Inscrever-me nesta trilha
                      </Button>
                    ) : nextStep ? (
                      <Button className="w-full" onClick={() => handleAdvance(detail.id)}>
                        <PlayCircle className="h-4 w-4 mr-2" />
                        Iniciar próximo curso: {nextStep.title}
                      </Button>
                    ) : (
                      <div className="text-center text-sm text-gray-600 bg-gray-50 rounded p-3">
                        Conclua o curso em andamento para liberar o próximo.
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrilhasTab;
